import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { DataService } from '../data.service';
import { NaploService } from './naplo.service';
import { KeretService } from '../keret.service';
import { NAPLO_FOMENUK, NaploFomenu, NaploMenupont, menupontCel, menupontLathato } from './naplo-menu';

export interface LathatoFomenu { f: NaploFomenu; pontok: NaploMenupont[]; }

/**
 *  A NAPLO főmenüjének állapota: mi látszik (jog + könyvelési mód + ügyféltípus), és melyik
 *  menüpont az aktív. A fejléc és a főmenü-oldal ugyanezt használja, hogy a kettő sose térjen el.
 */
@Injectable({ providedIn: 'root' })
export class NaploMenuService {
  lathato$ = new BehaviorSubject<LathatoFomenu[]>([]);
  /** Az éppen megnyitott oldalhoz tartozó menüpont kulcsa (kiemeléshez). */
  aktivPont$ = new BehaviorSubject<string | null>(null);

  constructor(private data: DataService, private naplo: NaploService, private router: Router, private keret: KeretService) {
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this.aktivKeres());
  }

  async frissit(): Promise<void> {
    let szabad: (u: string) => boolean = () => false;
    try { szabad = (await this.data.hozzaferesek()).szabad; } catch { /* jog nélkül üres a menü */ }
    const a = this.naplo.aktiv;
    const lista = NAPLO_FOMENUK
      .map((f) => ({ f, pontok: f.pontok.filter((p) => menupontLathato(p, a.konyveles_mod, a.tipus, szabad, !!a.bank_naplo)) }))
      .filter((x) => x.pontok.length);
    this.lathato$.next(lista);
    this.aktivKeres();
  }

  /** Menüpont megnyitása a könyvelési módnak megfelelő nézetben. */
  nyit(p: NaploMenupont): void {
    const c = menupontCel(p, this.naplo.aktiv.konyveles_mod);
    /*  Másik alrendszerbe vivő csempe (Tárgyi eszköz): a puszta navigálás a NAPLO-keretben hagyná a
     *  bal menüt, a fejlécet és a jogosultság-lekérést – az alrendszert is át kell váltani. */
    /*  ASZTALI PROGRAMBAN egy program = egy alrendszer: a másik alrendszer csempéje a MÁSIK telepített
     *  programot indítja el (vagy hozza előre) a saját hivatkozás-sémáján át (electron/main.js), nem vált
     *  és nem irányít át (user, 2026-10-07). Ha nincs telepítve, a program szól. */
    const asztali = (window as any).CRONOM_ASZTALI;
    if (p.alrendszer && asztali && String(asztali.mod || '').toUpperCase() !== p.alrendszer.toUpperCase()) {
      window.open(`cronom-${p.alrendszer.toLowerCase()}://megnyit`, '_blank');
      return;
    }
    if (p.alrendszer) {
      const k = this.keret.a;
      const mod = (k?.modok() || []).find((m) => m.name === p.alrendszer);
      if (k && mod) { k.modValt(mod, c.ut); return; }
    }
    this.router.navigate([c.ut], { queryParams: c.query || {} });
  }

  /** Melyik főmenühöz tartozik az aktív oldal (a fejléc ezt emeli ki). */
  aktivFomenu(): string | null {
    const k = this.aktivPont$.value;
    if (!k) return null;
    return this.lathato$.value.find((x) => x.pontok.some((p) => p.kulcs === k))?.f.kulcs || null;
  }

  private aktivKeres(): void {
    const t = this.router.parseUrl(this.router.url);
    const ut = '/' + (t.root.children['primary']?.segments.map((s) => s.path).join('/') || '');
    const q = t.queryParams || {};
    const mod = this.naplo.aktiv.konyveles_mod;
    // Az ELSŐ egyezés nyer (ugyanaz az oldal több főmenüben is szerepelhet, pl. a Bank napló
    // a Naplók és a Bank alatt – a kiemelés a menüsorrendben elsőé). A paraméteres egyezés
    // erősebb, mint a paraméter nélküli.
    let talalt: string | null = null;
    let talaltQ = false;
    for (const x of this.lathato$.value) {
      for (const p of x.pontok) {
        const c = menupontCel(p, mod);
        if (c.ut !== ut) continue;
        const illik = !c.query || Object.entries(c.query).every(([k, v]) => String(q[k] ?? '') === v);
        if (!illik) continue;
        if (!talalt || (!talaltQ && c.query)) { talalt = p.kulcs; talaltQ = !!c.query; }
      }
    }
    this.aktivPont$.next(talalt);
  }
}
